import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from '@hotjar/hotsauce';
import type { Site } from '@hotjar/site';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { NewPinModal } from '@hotjar/webapp/features/Heatmaps/components/NewPinModal/NewPinModal';
import styled from '@emotion/styled';
import type { GlobalFilter } from '@hotjar/global-filters';

const EmptyButton = styled(Button)`
  cursor: default !important;
`;

interface PinButtonProps {
  currentSite: Site | undefined;
  isDisabled: boolean;
  isHidden: boolean;
  isCurrentPagePinned: boolean;
  pattern: string;
  matchOperation: TargetingValue;
  filters: GlobalFilter;
}

export const PinButton = ({
  currentSite,
  isDisabled,
  isHidden,
  isCurrentPagePinned,
  pattern,
  matchOperation,
  filters,
}: PinButtonProps): JSX.Element | null => {
  const hasHeatmapsUpdatePermission = usePermission('heatmaps-update');
  const { t } = useTranslation('heatmaps');
  const [isNewPinModalOpen, setNewPinModalOpen] = useState(false);

  const openPinModal = () => setNewPinModalOpen(true);

  const closePinModal = () => setNewPinModalOpen(false);

  const isButtonDisabled = !hasHeatmapsUpdatePermission || isDisabled;

  if (isHidden) return null;

  return (
    <div>
      {isCurrentPagePinned ? (
        <EmptyButton kind="ghost" isDisabled>
          {t('save-button.saved')}
        </EmptyButton>
      ) : (
        <Tooltip
          text={t('save-button.tooltip-no-permission')}
          isDisabled={hasHeatmapsUpdatePermission}
        >
          <div>
            <Button onClick={openPinModal} isDisabled={isButtonDisabled}>
              {t('save-button.save')}
            </Button>
          </div>
        </Tooltip>
      )}
      {isNewPinModalOpen && (
        <NewPinModal
          isOpen={isNewPinModalOpen}
          onClose={closePinModal}
          currentSite={currentSite}
          pattern={pattern}
          matchOperation={matchOperation}
          filters={filters}
        />
      )}
    </div>
  );
};
